class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.initAccordions();

    // Check URL parameter contains '?redirectFromCartPage' to trigger cart drawer
    if (window.location.href.includes('?redirectFromCartPage')) {
      this.open();
    }
  }

  initAccordions() {
    this.addEventListener('click', (e) => {
      const summary = e.target.closest('[data-animated-accordion] > summary');
      if (!summary) return;

      const details = summary.closest('[data-animated-accordion]');
      const wrapper = details.querySelector('[data-accordion-content-wrapper]');
      if (!wrapper) return;

      e.preventDefault();

      if (details.open) {
        wrapper.classList.remove('is-expanded');
        const duration = parseFloat(getComputedStyle(wrapper).transitionDuration) || 0;
        if (duration > 0) {
          wrapper.addEventListener('transitionend', () => {
            details.open = false;
          }, { once: true });
        } else {
          details.open = false;
        }
      } else {
        details.open = true;
        requestAnimationFrame(() => {
          wrapper.classList.add('is-expanded');
        });
      }
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      
      if (window.dataLayerUtils) {
        window.dataLayerUtils.send('view_cart')
      }

      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        
        if (window.dataLayerUtils) {
          window.dataLayerUtils.send('view_cart')
        }

        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    
    // Refresh cart sections when drawer opens to ensure fresh data
    const cartDrawerItems = this.querySelector('cart-drawer-items') || this.querySelector('cart-items');
    if (cartDrawerItems && typeof cartDrawerItems.onCartUpdate === 'function' && !cartDrawerItems.isUpdating) {
      setTimeout(() => {
        if (!cartDrawerItems.isUpdating) {
          cartDrawerItems.onCartUpdate();
        }
      }, 50);
    }
    
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    // Debug: log entire cart when the drawer opens
    try {
      fetch('/cart.js', { 
        cache: 'no-store',
        headers: { 'X-Requested-With': 'XMLHttpRequest' } 
      })
        .then(r => r.json())
        .then(cart => {
          if (window.__GIFTPROPS_DEBUG__) {
            console.warn('%c[GiftBox Debug] cart.js on drawer open', 'color:#8A2BE2;font-weight:bold', cart);
          }
        })
        .catch(() => {});
    } catch(e) {}

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    // KRAKEN-4942: flag cart-drawer-open so the Klaviyo onsite popup can be hidden on mobile
    document.body.classList.add('cart-drawer-open');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.body.classList.remove('cart-drawer-open');

    // Refresh page if on a Family-Set product page so UI resets correctly
    if (document.querySelector('.family-set')) {
      // slight delay to allow closing animation to finish smoothly
      setTimeout(() => {
        window.location.reload();
      }, 150);
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.classList.remove('is-empty');
    this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
